import { memo, useState } from "react";

import {
  DataGridPremium,
  GridColDef,
  gridPageCountSelector,
  gridPageSelector,
  gridPaginationRowRangeSelector,
  gridRowCountSelector,
  gridPageSizeSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-premium";

import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";

import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";

import { Finding } from "stores/FindingStore";

import { FOTS, SUTS } from "utility/typescript";

// -----------------------------------------------------------------------------

const RegexPreviewTable = memo(function RegexPreviewTable({
  findings,
}: RegexPreviewTableProps): JSX.Element {
  const [pageSize, setPageSize] = useState(10);

  const columns: GridColDef[] = [
    { field: "uuid", headerName: "uuid", flex: 1 },
  ];

  return (
    <Box sx={{ height: 180, width: "100%" }}>
      <DataGridPremium
        getRowId={(row: SUTS<{ uuid: string }>) => row.uuid}
        rows={findings}
        columns={columns}
        pageSize={pageSize}
        rowsPerPageOptions={[5, 10, 50]}
        headerHeight={0}
        rowHeight={25}
        components={{ Pagination: CustomPagination }}
        sx={{
          "& .MuiTablePagination-root, & .MuiDataGrid-footerContainer": {
            minHeight: "10px",
            fontSize: "12px",
          },
          "& .MuiTablePagination-toolbar": {
            minHeight: "10px",
          },
        }}
        onPageSizeChange={(newSize: SUTS<number>) => setPageSize(newSize)}
      />
    </Box>
  );
});

export default RegexPreviewTable;

interface RegexPreviewTableProps {
  findings: Finding[];
}

// -----------------------------------------------------------------------------

const CustomPagination = () => {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const paginationRange = useGridSelector(
    apiRef,
    gridPaginationRowRangeSelector
  );
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
      }}
    >
      <Typography sx={{ fontSize: "12px", color: "text.secondary", mr: 1 }}>
        Rows per page:
      </Typography>

      <FormControl
        sx={{
          "& .MuiInputBase-root": {
            fontSize: "12px",

            "& #ignore-rule-type-select": {
              py: 0,
              pl: 0,
              pr: 2.5,
              color: "text.primary",
              borderRadius: 0,
            },

            "& .MuiSelect-icon": {
              right: 0,
            },

            "& .MuiOutlinedInput-notchedOutline": {
              border: 0,
            },

            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: 0,
            },
          },
        }}
      >
        <Select
          id="ignore-rule-type-select"
          value={pageSize}
          onChange={(e: FOTS) =>
            apiRef.current.setPageSize(Number(e?.target?.value))
          }
        >
          <MenuItem
            value={5}
            sx={{
              fontSize: "12px",
              px: 1,
              display: "flex",
              justifyContent: "center",
            }}
          >
            5
          </MenuItem>
          <MenuItem
            value={10}
            sx={{
              fontSize: "12px",
              px: 1,
              display: "flex",
              justifyContent: "center",
            }}
          >
            10
          </MenuItem>
          <MenuItem
            value={50}
            sx={{
              fontSize: "12px",
              px: 1,
              display: "flex",
              justifyContent: "center",
            }}
          >
            50
          </MenuItem>
        </Select>
      </FormControl>

      <Typography sx={{ fontSize: "12px", color: "text.primary", ml: 3 }}>
        {rowCount === 0 && <>0 of 0</>}
        {rowCount > 0 && (
          <>
            {(paginationRange?.firstRowIndex ?? 0) + 1} -{" "}
            {(paginationRange?.lastRowIndex ?? 0) + 1} of {rowCount}
          </>
        )}
      </Typography>

      <Box>
        <IconButton
          onClick={() => apiRef.current.setPage(page - 1)}
          disabled={page === 0}
          aria-label="previous page"
          size="small"
          sx={{ p: 0, mx: 0.5 }}
        >
          <KeyboardArrowLeft />
        </IconButton>
        <IconButton
          onClick={() => apiRef.current.setPage(page + 1)}
          disabled={pageCount < 2 || page + 1 === pageCount}
          aria-label="next page"
          size="small"
          sx={{ p: 0, mr: 0.5 }}
        >
          <KeyboardArrowRight />
        </IconButton>
      </Box>
    </Box>
  );
};
